/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { Controller, useFormContext } from "react-hook-form";
import { Button, FieldGroup, Form, Input } from "core/components";
import { toTitleCase, toBase64, translate } from "core/helpers";

type DocumentModalProps = {
  title: string;
  show: boolean;
  isView: boolean;
  readOnly: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const DocumentModal = ({ index, show, isView, readOnly, onClose, onConfirm }: DocumentModalProps): JSX.Element => {
  const {
    control,
    unregister,
    setValue,
    trigger,
    watch,
    setError,
    resetField,
    formState: { errors }
  } = useFormContext();
  const fieldName = `documents_attributes[${index}]` as const;
  const fieldErrors = errors.documents_attributes?.[index];

  const document = watch(fieldName);
  const { document_type, file_name } = document;
  const documentType = translate(document_type);
  const title = file_name ? `View ${documentType}` : `Add ${documentType}`;

  const onChange = (event) => {
    const file = event.target.files[0] as File;

    if (file) {
      toBase64(file, (result) => {
        setValue(`${fieldName}.document_data_uri`, result);
        setValue(`${fieldName}.file_name`, file.name);
      });
    } else {
      setValue(`${fieldName}.document_data_uri`, null);
      setValue(`${fieldName}.file_name`, null);
    }
  };

  return (
    <React.StrictMode>
      <Modal show={show}>
        <Modal.Header>{title}</Modal.Header>
        <Modal.Body>
          <Form>
            <Container>
              <Row>
                <Col>
                  {readOnly || isView ? (
                    <Input
                      label="File"
                      className="form-control"
                      placeholder="STD-01MIRANDA-1001"
                      errorMessage={fieldErrors?.document_data_uri?.message}
                      value={document?.file_name || ""}
                      readOnly={readOnly || isView}
                    />
                  ) : (
                    <Input
                      label="File"
                      type="file"
                      accept="application/pdf"
                      required
                      className="form-control"
                      placeholder="STD-01MIRANDA-1001"
                      errorMessage={fieldErrors?.document_data_uri?.message}
                      onChange={onChange}
                    />
                  )}
                  <Controller
                    name={`${fieldName}.description`}
                    control={control}
                    render={({ field }) => (
                      <FieldGroup label="Description" errorMessage={fieldErrors?.description?.message}>
                        <textarea
                          {...field}
                          className="field"
                          placeholder={`${toTitleCase(document_type)} for STD-01MIRANDA-1001`}
                          maxLength={255}
                          value={field.value || ""}
                          readOnly={readOnly || isView}
                        />
                      </FieldGroup>
                    )}
                  />
                  <Controller
                    name={`${fieldName}.remarks`}
                    control={control}
                    render={({ field }) => (
                      <FieldGroup label="Remarks" errorMessage={fieldErrors?.remarks?.message}>
                        <textarea
                          {...field}
                          className="field"
                          placeholder={`${toTitleCase(document_type)} remarks`}
                          maxLength={255}
                          value={field.value || ""}
                          readOnly={readOnly || isView}
                        />
                      </FieldGroup>
                    )}
                  />
                </Col>
              </Row>
            </Container>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              resetField(fieldName, { keepError: false, defaultValue: { ...document } });
              onClose();
            }}
            icon="times"
            title="Close"
          />
          {!readOnly && !isView && (
            <Button
              className="button--brand"
              icon="save"
              title="Save"
              onClick={async () => {
                setError(`${fieldName}.document_data_uri`, "Document is required.");
                const isValid = await trigger([fieldName]);
                if (isValid) {
                  unregister(`${fieldName}.document`);
                  onConfirm();
                }
              }}
            />
          )}
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default DocumentModal;
